import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../Navbar";
import TreatImage from "./TreatImage";
import { getReceivedTreats, reportTreatGreeting, sendTreatThanks } from "../../services/treatApi";
import { formatMoney } from "../../services/proSystemApi";
import "../Pro/Pro.css";
import "./Treat.css";

// =====================================================
// MY RECEIVED TREATS — blogger side  (Steps 7, 8)
//
// Every treat appears as soon as the server has verified the reader's
// payment; nothing waits for a payout. Two statuses are shown and never
// merged:
//   Payment — the reader's payment (always "Successful" on this page)
//   Payout  — the blogger's money. Blank until the admin starts a payout;
//             "Pending" while it is in progress; "Paid" only once the admin
//             confirms the transfer.
// Each treat has a one-time "Send Thanks" (≤ 50 words) and, when it carries a
// greeting, "Report".
// =====================================================

const THANKS_MAX_WORDS = 50;

const REASONS = [
  { key: "abusive", label: "Abusive or insulting" },
  { key: "harassment", label: "Harassment" },
  { key: "sexual", label: "Sexual content" },
  { key: "hate", label: "Hate speech" },
  { key: "spam", label: "Spam" },
  { key: "other", label: "Something else" },
];

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const countWords = (text) => text.trim().split(/\s+/).filter(Boolean).length;

const PAYOUT_LABEL = { processing: "Pending", paid: "Paid" };
const PAYOUT_CLASS = { processing: "pending", paid: "paid" };

function MyReceivedTreats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [reporting, setReporting] = useState(null); // the treat being reported
  const [reason, setReason] = useState("abusive");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [reportError, setReportError] = useState("");

  const [thanking, setThanking] = useState(null); // the treat being thanked
  const [thanks, setThanks] = useState("");
  const [thanksError, setThanksError] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await getReceivedTreats());
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load your treats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const openReport = (treat) => {
    setReporting(treat);
    setReason("abusive");
    setDetails("");
    setReportError("");
  };

  const submitReport = async () => {
    if (!reporting) return;
    setSending(true);
    setReportError("");
    try {
      const res = await reportTreatGreeting(reporting._id, reason, details.trim());
      setReporting(null);
      setToast({ type: "success", msg: res?.message || "Reported." });
      load();
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not send the report";
      if (err?.response?.status === 409) {
        setReporting(null);
        setToast({ type: "warn", msg });
        load();
      } else {
        setReportError(msg);
      }
    } finally {
      setSending(false);
    }
  };

  const openThanks = (treat) => {
    setThanking(treat);
    setThanks("");
    setThanksError("");
  };

  const words = countWords(thanks);

  const submitThanks = async () => {
    if (!thanking) return;
    setSending(true);
    setThanksError("");
    try {
      const res = await sendTreatThanks(thanking._id, thanks.trim());
      setThanking(null);
      setToast({ type: "success", msg: res?.message || "Thank-you sent." });
      load();
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not send your thank-you";
      if (err?.response?.status === 409) {
        setThanking(null);
        setToast({ type: "warn", msg });
        load();
      } else {
        setThanksError(msg);
      }
    } finally {
      setSending(false);
    }
  };

  const summary = data?.summary;

  return (
    <div className="pro-page-wrap">
      <Navbar />
      {toast && <div className={`pro-toast pro-toast-${toast.type}`}>{toast.msg}</div>}

      <div className="pro-container">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">🎁 MY RECEIVED TREATS</div>
          <h1 className="pro-hero-title">Treats from your readers</h1>
          {data && <p className="pro-hero-sub">{data.count} treat{data.count === 1 ? "" : "s"} received</p>}
        </header>

        {loading && (
          <div className="pro-loading-screen">
            <div className="pro-spinner" />
            <p>Loading…</p>
          </div>
        )}

        {!loading && error && (
          <div className="pro-card pro-empty">
            <p>{error}</p>
            <button type="button" className="pro-btn pro-btn-primary" onClick={load}>
              Try again
            </button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {data.payoutSetup && data.payoutSetup.status !== "verified" && (
              <div className="pro-card pro-notice pro-notice-warn">
                <p>
                  {data.payoutSetup.complete
                    ? "Your payout details are waiting for admin verification. Your earnings stay in your balance until then."
                    : data.payoutSetup.status === "rejected"
                      ? "Your payout details were rejected. Please correct and resubmit them to receive payouts."
                      : "Complete Payout Setup to receive your treat earnings."}{" "}
                  <Link to="/treat-payouts">Set Up Payouts →</Link>
                </p>
              </div>
            )}

            <section className="pro-money-grid" aria-label="Treat earnings summary">
              <div className="pro-money-card pro-money-primary">
                <small>Your share received</small>
                <strong>{formatMoney(summary.receivedRupees)}</strong>
                <span className="pro-money-sub">90% of each treat</span>
              </div>
              <div className="pro-money-card">
                <small>Balance (awaiting payout)</small>
                <strong>{formatMoney(summary.balanceRupees)}</strong>
              </div>
              <div className="pro-money-card">
                <small>Pending Payout</small>
                {/* Blank when no payout has been raised — not "₹0.00". */}
                <strong data-testid="treat-pending">
                  {summary.pendingRupees == null ? "" : formatMoney(summary.pendingRupees)}
                </strong>
              </div>
              <div className="pro-money-card">
                <small>Paid Out</small>
                <strong>{formatMoney(summary.paidRupees)}</strong>
              </div>
            </section>

            {data.items.length === 0 ? (
              <div className="pro-card pro-empty">
                <p>No treats yet. When a reader buys you a treat from one of your posts, it shows up here straight away.</p>
              </div>
            ) : (
              <div className="pro-table-scroll">
                <table className="pro-table">
                  <thead>
                    <tr>
                      <th>Reader</th>
                      <th>Treat</th>
                      <th>Price</th>
                      <th>Blog post</th>
                      <th>Greeting</th>
                      <th>Date and time</th>
                      <th>Payment</th>
                      <th>Payout</th>
                      <th>Thanks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((row) => (
                      <tr key={row._id}>
                        <td>
                          <div className="pro-table-user">
                            {row.reader.profilePic ? (
                              <img src={row.reader.profilePic} alt={row.reader.name} />
                            ) : (
                              <span className="pro-blog-avatar-fallback">
                                {(row.reader.name || "?").charAt(0).toUpperCase()}
                              </span>
                            )}
                            <span>{row.reader.name}</span>
                          </div>
                        </td>
                        <td>
                          <span className="treat-cell-treat">
                            <TreatImage image={row.treat.image} name={row.treat.name} icon={row.treat.icon} size={32} />
                            {row.treat.name}
                          </span>
                        </td>
                        <td>
                          <span className="pro-table-strong">{formatMoney(row.priceRupees)}</span>
                          <small className="pro-table-sub">You get {formatMoney(row.yourShareRupees)}</small>
                        </td>
                        <td>{row.blog.title || "—"}</td>
                        <td className="treat-cell-message">
                          {row.message ? (
                            <>
                              <span>“{row.message}”</span>
                              {row.reportStatus ? (
                                <small className="pro-table-sub">Reported</small>
                              ) : (
                                <button type="button" className="treat-report-link" onClick={() => openReport(row)}>
                                  ⚑ Report
                                </button>
                              )}
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          {formatDateTime(row.paidAt)}
                          <small className="pro-table-sub treat-mono">{row.reference || ""}</small>
                        </td>
                        <td>
                          <span className="pro-status-pill pro-status-paid">Successful</span>
                        </td>
                        <td>
                          {row.payoutStatus ? (
                            <span className={`pro-status-pill pro-status-${PAYOUT_CLASS[row.payoutStatus] || row.payoutStatus}`}>
                              {PAYOUT_LABEL[row.payoutStatus] || row.payoutStatus}
                            </span>
                          ) : null}
                        </td>
                        <td className="treat-cell-message">
                          {row.thanks ? (
                            <small className="pro-table-sub">Sent: “{row.thanks.message}”</small>
                          ) : (
                            <button type="button" className="pro-btn pro-btn-sm pro-btn-ghost" onClick={() => openThanks(row)}>
                              Send Thanks
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="pro-card pro-notice">
              <p>
                A treat shows here as soon as the reader's payment is verified. Your 90% share is paid out by
                the admin to your verified bank account — the Payout column changes to Paid once the transfer
                is confirmed.
              </p>
            </div>
          </>
        )}
      </div>

      {thanking && (
        <div className="pro-modal-overlay" role="dialog" aria-modal="true" aria-label="Send thanks">
          <div className="pro-modal treat-report-modal">
            <h3>Send thanks to {thanking.reader.name}</h3>
            <p>You can send one thank-you for this treat. Only {thanking.reader.name} will see it.</p>
            <label className="treat-label" htmlFor="treat-thanks">Message</label>
            <textarea
              id="treat-thanks"
              className="pro-input pro-textarea"
              rows={4}
              maxLength={400}
              value={thanks}
              onChange={(e) => setThanks(e.target.value)}
              placeholder="Thank you so much for supporting my blog…"
              disabled={sending}
            />
            <div className={`treat-count${words > THANKS_MAX_WORDS ? " treat-count-over" : ""}`}>
              {words}/{THANKS_MAX_WORDS} words
            </div>
            {thanksError && <p className="pro-error">{thanksError}</p>}
            <div className="treat-actions">
              <button type="button" className="pro-btn pro-btn-ghost" onClick={() => setThanking(null)} disabled={sending}>
                Cancel
              </button>
              <button
                type="button"
                className="pro-btn pro-btn-primary"
                onClick={submitThanks}
                disabled={sending || words === 0 || words > THANKS_MAX_WORDS}
              >
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {reporting && (
        <div className="pro-modal-overlay" role="dialog" aria-modal="true" aria-label="Report greeting">
          <div className="pro-modal treat-report-modal">
            <h3>Report this greeting</h3>
            <p>
              From <strong>{reporting.reader.name}</strong>: “{reporting.message}”
            </p>

            <label className="treat-label" htmlFor="treat-report-reason">Reason</label>
            <select
              id="treat-report-reason"
              className="pro-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={sending}
            >
              {REASONS.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>

            <label className="treat-label" htmlFor="treat-report-details">
              Details {reason !== "other" && <span className="pro-optional">(optional)</span>}
            </label>
            <textarea
              id="treat-report-details"
              className="pro-input pro-textarea"
              rows={3}
              maxLength={500}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              disabled={sending}
            />

            {reportError && <p className="pro-error">{reportError}</p>}

            <div className="treat-actions">
              <button type="button" className="pro-btn pro-btn-ghost" onClick={() => setReporting(null)} disabled={sending}>
                Cancel
              </button>
              <button
                type="button"
                className="pro-btn pro-btn-primary"
                onClick={submitReport}
                disabled={sending || (reason === "other" && !details.trim())}
              >
                {sending ? "Sending…" : "Report"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyReceivedTreats;
